import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Plus } from "lucide-react";
import ArticleForm, { LANGS, suffixFor } from "./articles/ArticleForm";
import ArticleRow from "./articles/ArticleRow";

const EMPTY_FORM = {
  title: "", title_bg: "", title_es: "",
  slug: "", excerpt: "", excerpt_bg: "", excerpt_es: "",
  content: "", content_bg: "", content_es: "",
  seo_title: "", seo_description: "",
  seo_title_bg: "", seo_description_bg: "",
  seo_title_es: "", seo_description_es: "",
  category: "general", published: false, tags: [],
};

function normalizeForEdit(article) {
  const form = { ...article };
  LANGS.forEach((l) => {
    const s = suffixFor(l.code);
    ["title", "excerpt", "content", "seo_title", "seo_description"].forEach((k) => {
      if (!form[`${k}${s}`]) form[`${k}${s}`] = "";
    });
  });
  return form;
}

export default function ArticlesManager({ API, isDark, articles, fetchArticles, editArticle, setEditArticle, articleForm, setArticleForm }) {
  const [showForm, setShowForm] = useState(false);
  const [langTab, setLangTab] = useState("bg");
  const [showSeo, setShowSeo] = useState(false);
  const [translating, setTranslating] = useState(null);

  const resetForm = () => {
    setArticleForm(EMPTY_FORM);
    setEditArticle(null);
    setShowForm(false);
    setLangTab("bg");
  };

  const openEdit = (a) => {
    setArticleForm(normalizeForEdit(a));
    setEditArticle(a);
    setShowForm(true);
  };

  const saveArticle = async () => {
    try {
      if (editArticle) {
        await axios.put(`${API}/articles/${editArticle.id}`, articleForm);
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/articles`, articleForm);
        toast.success("Article created");
      }
      resetForm();
      fetchArticles();
    } catch {
      toast.error("Error saving article");
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await axios.delete(`${API}/articles/${id}`);
      toast.success("Article deleted");
      fetchArticles();
    } catch {
      toast.error("Error deleting");
    }
  };

  const togglePublish = async (a) => {
    try {
      await axios.put(`${API}/articles/${a.id}`, { published: !a.published });
      fetchArticles();
    } catch {
      toast.error("Error");
    }
  };

  const translateArticle = async (articleId) => {
    setTranslating(articleId);
    try {
      await axios.post(`${API}/articles/${articleId}/translate`);
      toast.success("Translated to EN + ES");
      fetchArticles();
    } catch {
      toast.error("Translation error");
    } finally {
      setTranslating(null);
    }
  };

  return (
    <Card className={isDark ? "bg-gray-800 border-gray-700" : "card-elevated border-0"} data-testid="articles-manager">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#6366F1]" /> Articles ({articles.length})
          </CardTitle>
          <Button size="sm" className="gradient-primary text-white" onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <ArticleForm
            form={articleForm}
            setForm={setArticleForm}
            langTab={langTab}
            setLangTab={setLangTab}
            showSeo={showSeo}
            setShowSeo={setShowSeo}
            onCancel={resetForm}
            onSave={saveArticle}
            isEditing={Boolean(editArticle)}
            isDark={isDark}
          />
        )}

        <div className="space-y-2">
          {articles.map((a) => (
            <ArticleRow
              key={a.id}
              article={a}
              isDark={isDark}
              translating={translating}
              onTranslate={translateArticle}
              onTogglePublish={togglePublish}
              onEdit={openEdit}
              onDelete={deleteArticle}
            />
          ))}
          {articles.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No articles yet. Click "New" to create one.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
